import { type ReactNode, useMemo } from 'react';

interface UsePaginationArguments {
  currentPageIndex: number;
  totalPages: number;
  maxNumOfPageButtons?: number;
  render: (index: number) => ReactNode;
}

export const usePaginationRange = (args: UsePaginationArguments) => {
  return useMemo(() => {
    const { currentPageIndex, totalPages, maxNumOfPageButtons = 7, render } = args;
    const items = [];

    /**
     * Inititally, try to show 3 pages above and below the current page.
     * This helps gravitate towards showing "current" page in the center
     * of 7 page buttons.
     */
    const prevNextCount = Math.floor(maxNumOfPageButtons / 2);

    // Show max of 7 page buttons
    const targetRange = Math.min(maxNumOfPageButtons, totalPages);

    /**
     * Initial guesses
     *
     * Try to set the min page index to 3 below the current.
     * Try to set the max page index to 3 above the current.
     */
    let min = Math.max(0, currentPageIndex - prevNextCount);
    let max = Math.min(currentPageIndex + prevNextCount, totalPages - 1);

    /**
     * Pad the left
     *
     * If min index is not the first page
     * and we still don't have 7 page buttons,
     * decrease the min index so that we show more page buttons.
     */
    if (min > 0 && max - min < targetRange - 1) {
      min -= targetRange - (max - min) - 1;
    }

    /**
     * Pad the right
     *
     * If max index is not the last page
     * and we still don't have 7 page buttons,
     * increase the max index so that we show more page buttons.
     */
    if (max < totalPages - 1 && max - min < targetRange - 1) {
      max += targetRange - (max - min) - 1;
    }

    for (let i = min; i <= max; i += 1) {
      items.push(render(i));
    }

    return items;
  }, [args]);
};
